/**
 * API KYC Provider
 *
 * Implements KYCDataProvider using the BFF API with axios.
 * Used for production and staging environments.
 */

import { normalizeCountry } from "../../../config/fieldConfig/optionLists";
import type {
	KYCContact,
	KYCDirector,
	KYCFormData,
	KYCLicense,
	KYCOwnershipTier,
} from "../../../types";
import axiosClient from "../../api/axiosClient";
import type { KYCDataProvider } from "../types";

interface PaginatedResponse<T> {
	data: T[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
}

/**
 * DBKYBForm format (snake_case from BFF) to KYCFormData format (camelCase for UI)
 */
interface DBKYBForm {
	id: string;
	wallet_id?: string | null;
	status:
		| "draft"
		| "in-progress"
		| "submitted"
		| "under_review"
		| "approved"
		| "rejected";
	current_step: number;
	// Step 1
	business_legal_name?: string;
	dba?: string;
	previous_business_names?: string;
	entity_type?: string;
	country_of_incorporation?: string;
	registration_number?: string;
	date_of_incorporation?: string;
	registered_address?: Record<string, unknown>;
	website?: string;
	business_description?: string;
	expected_monthly_volume?: number;
	proof_of_business_url?: string;
	// Step 2
	wallet_type?: string;
	hosted_status?: string;
	integrated_with_stellar?: boolean;
	allow_p2p_internal?: boolean;
	allow_p2p_external?: boolean;
	virtual_assets_info?: string;
	public_key_sharing?: string;
	is_wallet_owner_kyc_required?: boolean;
	wallet_holder_info_collection?: string;
	wallet_holder_update_circumstances?: string;
	wallet_holder_update_frequency?: string;
	is_wallet_holder_info_updated?: boolean;
	omnibus_account_usage?: string;
	other_funding_methods?: string;
	// Step 3 — beneficial owners stored at root `tiers`. The historical
	// `ownership_data` nested object is unused (no document carries it);
	// retained on the interface for backwards compatibility with any legacy
	// rows that may still reference it.
	tiers?: unknown[];
	number_of_tiers?: number;
	ownership_data?: Record<string, unknown>;
	consent_background_checks?: boolean;
	any_owner_convicted?: boolean;
	conviction_details?: string;
	has_board_of_directors?: boolean;
	board_of_directors?: unknown;
	// Step 4
	is_regulated_entity?: boolean;
	licenses_registrations?: Record<string, unknown>;
	compliance_officer?: Record<string, unknown>;
	sanctions_screening?: boolean;
	sanctions_vendor?: string;
	sanctions_what_screened?: string;
	blockchain_monitoring?: boolean;
	blockchain_vendor?: string;
	blockchain_process?: string;
	blockchain_thresholds?: string;
	enforcement_actions?: boolean;
	enforcement_details?: string;
	risk_assessment_url?: string;
	compliance_program_url?: string;
	// Step 5
	velocity_limits?: string;
	transaction_limits?: string;
	other_limits?: string;
	blocks_wallet_holders?: boolean;
	block_circumstances?: string;
	prevention_controls?: string;
	// Step 6
	primary_business_contact?: Record<string, unknown>;
	primary_technical_contact?: Record<string, unknown>;
	primary_legal_contact?: Record<string, unknown>;
	primary_signer?: Record<string, unknown>;
	ownership_chart_url?: string;
	// Metadata
	submitted_at?: string;
	submitted_by?: string | null;
	reviewed_by?: string | null;
	reviewed_at?: string;
	reviewer_notes?: string;
	comments?: Array<{
		id: string;
		author: string;
		text: string;
		timestamp: string;
		is_internal: boolean;
	}>;
	created_at: string;
	updated_at: string;
}

/**
 * Transform DBKYBForm (snake_case) to KYCFormData (camelCase).
 * Used when receiving from tRPC or REST; form components expect camelCase.
 */
export function transformDBKYBToKYCFormData(
	dbKyb: DBKYBForm & { id?: string },
): KYCFormData {
	// Helper to safely extract nested object
	const extractContact = (contact?: Record<string, unknown>): KYCContact => {
		if (!contact || typeof contact !== "object") {
			return { name: "", email: "", phone: "", role: "" };
		}
		return {
			name: (contact.name as string) || "",
			email: (contact.email as string) || "",
			phone: (contact.phone as string) || "",
			role: (contact.role as string) || "",
		};
	};

	// Helper to extract ownership tiers (normalize nationality for legacy display names)
	const extractOwnershipTiers = (tiersFromDb?: unknown): KYCOwnershipTier[] => {
		// DB schema stores beneficial-owner records at root `tiers`.
		// Earlier code read from `dbKyb.ownership_data` which never exists on
		// the document — that returned `[]` regardless of saved data, and the
		// resulting empty form state was then written back to the server on
		// the next save, wiping owners. See RAMPS-XXXX post-mortem.
		if (!Array.isArray(tiersFromDb)) {
			return [];
		}
		return (tiersFromDb as KYCOwnershipTier[]).map((tier) => ({
			...tier,
			nationality: tier.nationality
				? normalizeCountry(tier.nationality)
				: tier.nationality,
			ultimateBeneficialOwners: tier.ultimateBeneficialOwners?.map((ubo) => ({
				...ubo,
				nationality: ubo.nationality
					? normalizeCountry(ubo.nationality)
					: ubo.nationality,
			})),
		}));
	};

	// Helper to extract board of directors (normalize nationality for legacy)
	const extractBoardOfDirectors = (directors?: unknown): KYCDirector[] => {
		if (!directors || !Array.isArray(directors)) {
			return [];
		}
		return (directors as KYCDirector[]).map((d) => ({
			...d,
			nationality: d.nationality
				? normalizeCountry(d.nationality)
				: d.nationality,
		}));
	};

	// Helper to extract licenses
	const extractLicenses = (
		licensesData?: Record<string, unknown>,
	): KYCLicense[] => {
		if (!licensesData || typeof licensesData !== "object") {
			return [];
		}
		if (Array.isArray(licensesData.licenses)) {
			return licensesData.licenses as KYCLicense[];
		}
		return [];
	};

	// Helper to extract registered address
	const extractRegisteredAddress = (address?: Record<string, unknown>) => {
		if (!address || typeof address !== "object") {
			return {
				address1: "",
				address2: "",
				city: "",
				stateProvince: "",
				zip: "",
				country: "",
			};
		}
		return {
			address1: (address.address1 as string) || "",
			address2: (address.address2 as string) || "",
			city: (address.city as string) || "",
			stateProvince:
				(address.state_proince as string) ||
				(address.stateProvince as string) ||
				"",
			zip: (address.zip as string) || "",
			country: normalizeCountry(address.country as string | undefined),
		};
	};

	// Map status from snake_case to camelCase/kebab-case
	const mapStatus = (status: string): KYCFormData["status"] => {
		const statusMap: Record<string, KYCFormData["status"]> = {
			draft: "draft",
			"in-progress": "in-progress",
			submitted: "submitted",
			under_review: "under_review",
			approved: "approved",
			rejected: "rejected",
		};
		return statusMap[status] || "draft";
	};

	// Use stored progress when present; otherwise derive from current_step
	const percentComplete =
		typeof (dbKyb as unknown as Record<string, unknown>).percent_complete ===
		"number"
			? Math.min(
					Math.max(
						(dbKyb as unknown as Record<string, unknown>)
							.percent_complete as number,
						0,
					),
					100,
				)
			: Math.min(Math.max((dbKyb.current_step / 6) * 100, 0), 100);
	const lastSavedAt =
		(dbKyb as unknown as Record<string, unknown>).last_saved_at != null
			? String((dbKyb as unknown as Record<string, unknown>).last_saved_at)
			: dbKyb.updated_at;

	return {
		// Step 1 - Company Basics
		businessLegalName: dbKyb.business_legal_name || "",
		dba: dbKyb.dba || "",
		previousBusinessNames: dbKyb.previous_business_names || "",
		entityType: dbKyb.entity_type || "",
		countryOfIncorporation: normalizeCountry(dbKyb.country_of_incorporation),
		registrationNumber: dbKyb.registration_number || "",
		dateOfIncorporation: dbKyb.date_of_incorporation || "",
		registeredAddress: extractRegisteredAddress(dbKyb.registered_address),
		website: dbKyb.website || "",
		businessDescription: dbKyb.business_description || "",
		expectedMonthlyVolume: dbKyb.expected_monthly_volume || 0,
		proofOfBusinessUrl: dbKyb.proof_of_business_url,
		proofOfAddressUrl: (dbKyb as unknown as Record<string, unknown>)
			.proof_of_address_url as string | undefined,
		federalTaxId: (dbKyb as unknown as Record<string, unknown>)
			.federal_tax_id as string | undefined,
		vatId: (dbKyb as unknown as Record<string, unknown>).vat_id as
			| string
			| undefined,
		rutDocument: (dbKyb as unknown as Record<string, unknown>).rut_document as
			| string
			| undefined,

		// Step 2 - Wallet & Technical Details
		walletType: (dbKyb.wallet_type as KYCFormData["walletType"]) || "Custodial",
		hostedStatus:
			(dbKyb.hosted_status as KYCFormData["hostedStatus"]) || "Hosted",
		currentlyIntegratedWithStellar: dbKyb.integrated_with_stellar || false,
		allowP2PInternal: dbKyb.allow_p2p_internal || false,
		allowP2PExternal: dbKyb.allow_p2p_external || false,
		virtualAssetsDetails: dbKyb.virtual_assets_info || "",
		publicKeySharing:
			(dbKyb.public_key_sharing as KYCFormData["publicKeySharing"]) ||
			"Individual",
		isWalletOwnerKYCRequired: dbKyb.is_wallet_owner_kyc_required || false,
		walletHolderInfoCollected: dbKyb.wallet_holder_info_collection,
		walletHolderInfoUpdateCircumstances:
			dbKyb.wallet_holder_update_circumstances,
		walletHolderInfoUpdateFrequency: dbKyb.wallet_holder_update_frequency,
		isWalletHolderInfoUpdatedRegularly:
			dbKyb.is_wallet_holder_info_updated || false,
		omnibusAccountDescription: dbKyb.omnibus_account_usage,
		otherFundingMethods: dbKyb.other_funding_methods,

		// Step 3 - Ownership & UBO
		numberOfOwnershipTiers: extractOwnershipTiers(dbKyb.tiers).length,
		ownershipTiers: extractOwnershipTiers(dbKyb.tiers),
		consentForBackgroundChecks: dbKyb.consent_background_checks || false,
		anyOwnerConvicted: dbKyb.any_owner_convicted || false,
		convictionDetails: dbKyb.conviction_details,
		hasBoardOfDirectors: dbKyb.has_board_of_directors,
		boardOfDirectors: extractBoardOfDirectors(dbKyb.board_of_directors),

		// Step 4 - Compliance & Risk
		isRegulatedEntity: dbKyb.is_regulated_entity || false,
		licenses: extractLicenses(dbKyb.licenses_registrations),
		complianceOfficer: {
			...extractContact(dbKyb.compliance_officer),
			photoIdUrl:
				(dbKyb.compliance_officer?.photo_id_url as string) || undefined,
		},
		sanctionsScreening: dbKyb.sanctions_screening || false,
		sanctionsVendor: dbKyb.sanctions_vendor,
		sanctionsScreenedItems: dbKyb.sanctions_what_screened,
		blockchainMonitoring: dbKyb.blockchain_monitoring || false,
		monitoringVendor: dbKyb.blockchain_vendor,
		monitoringProcess: dbKyb.blockchain_process,
		monitoringThresholds: dbKyb.blockchain_thresholds,
		regulatoryEnforcement: dbKyb.enforcement_actions || false,
		enforcementDetails: dbKyb.enforcement_details,
		riskAssessmentUrl: dbKyb.risk_assessment_url,
		complianceProgramUrl: dbKyb.compliance_program_url,

		// Step 5 - Limits & Restrictions
		velocityLimits: dbKyb.velocity_limits || "",
		transactionLimits: dbKyb.transaction_limits || "",
		otherLimits: dbKyb.other_limits || "",
		blockWalletHolders: dbKyb.blocks_wallet_holders || false,
		blockingCircumstances: dbKyb.block_circumstances,
		blockingPreventionControls: dbKyb.prevention_controls,

		// Step 6 - Contacts & Final Uploads
		primaryBusinessContact: extractContact(dbKyb.primary_business_contact),
		primaryProductContact: extractContact(dbKyb.primary_technical_contact),
		primaryLegalContact: extractContact(dbKyb.primary_legal_contact),
		primarySigner: extractContact(dbKyb.primary_signer),
		ownershipStructureChartUrl: dbKyb.ownership_chart_url,

		// Metadata
		id: dbKyb.id ?? "",
		walletId: dbKyb.wallet_id || undefined,
		submittedBy: dbKyb.submitted_by || undefined,
		currentStep: dbKyb.current_step,
		percentComplete,
		lastSavedAt,
		status: mapStatus(dbKyb.status),
		submittedAt: dbKyb.submitted_at,
		approvedBy: dbKyb.reviewed_by || undefined,
		approvedAt: dbKyb.reviewed_at,
		reviewerComments: (() => {
			if (
				Array.isArray((dbKyb as unknown as Record<string, unknown>).comments)
			) {
				const comments = (dbKyb as unknown as Record<string, unknown>)
					.comments as Array<{
					id: string;
					author: string;
					text: string;
					timestamp: string;
					is_internal: boolean;
				}>;
				return comments.map((c) => ({
					id: c.id,
					author: c.author,
					text: c.text,
					timestamp: c.timestamp,
					isInternal: c.is_internal,
				}));
			}
			if (dbKyb.reviewer_notes) {
				return [
					{
						id: "1",
						author: dbKyb.reviewed_by || "Admin",
						text: dbKyb.reviewer_notes,
						timestamp: dbKyb.reviewed_at || dbKyb.updated_at,
						isInternal: true,
					},
				];
			}
			return undefined;
		})(),
		createdAt: dbKyb.created_at,
		updatedAt: dbKyb.updated_at,
		hiddenFieldValues: (dbKyb as unknown as Record<string, unknown>)
			.hidden_field_values as Record<string, unknown> | undefined,
	};
}

export const apiKycProvider: KYCDataProvider = {
	async getAll(): Promise<KYCFormData[]> {
		// Interceptor returns PaginatedResponse as-is (no success wrapper)
		const response = (await axiosClient.get(
			"/api/kyb-forms",
		)) as unknown as PaginatedResponse<DBKYBForm>;
		if (response && "data" in response && Array.isArray(response.data)) {
			return response.data.map(transformDBKYBToKYCFormData);
		}
		// Fallback: assume it's already an array
		return Array.isArray(response)
			? (response as unknown as DBKYBForm[]).map(transformDBKYBToKYCFormData)
			: [];
	},

	async getById(id: string): Promise<KYCFormData | null> {
		try {
			// Interceptor unwraps { success, data } — result is the DBKYBForm directly
			const data = (await axiosClient.get(
				`/api/kyb-forms/${id}`,
			)) as unknown as DBKYBForm;
			if (!data) return null;
			return transformDBKYBToKYCFormData(data);
		} catch (error: unknown) {
			if (
				error &&
				typeof error === "object" &&
				"status" in error &&
				error.status === 404
			) {
				return null;
			}
			throw error;
		}
	},

	async getDraftByWalletId(walletId: string): Promise<KYCFormData | null> {
		try {
			// Interceptor returns PaginatedResponse as-is (no success wrapper)
			const response = (await axiosClient.get(
				`/api/kyb-forms?walletId=${encodeURIComponent(walletId)}&status=draft&limit=1`,
			)) as unknown as PaginatedResponse<DBKYBForm>;
			const forms =
				response && "data" in response && Array.isArray(response.data)
					? response.data
					: [];
			const first = forms[0];
			return first ? transformDBKYBToKYCFormData(first) : null;
		} catch {
			return null;
		}
	},

	async create(form: KYCFormData): Promise<KYCFormData> {
		// Transform KYCFormData to DBKYBForm format for request
		const requestBody: Partial<DBKYBForm> = {
			wallet_id: form.walletId || null,
			status: "draft",
			current_step: form.currentStep || 1,
		};
		// Interceptor unwraps { success, data } — result is the DBKYBForm directly
		const data = (await axiosClient.post(
			"/api/kyb-forms",
			requestBody,
		)) as unknown as DBKYBForm;
		return transformDBKYBToKYCFormData(data);
	},

	async delete(id: string): Promise<void> {
		await axiosClient.delete(`/api/kyb-forms/${id}`);
	},

	async update(
		id: string,
		updates: Partial<KYCFormData>,
	): Promise<KYCFormData> {
		// Send all fields as camelCase - BFF will convert to snake_case automatically
		// Exclude metadata fields that shouldn't be updated via PATCH
		const requestBody: Record<string, unknown> = {};

		// Step 1 - Company Basics
		if (updates.businessLegalName !== undefined)
			requestBody.businessLegalName = updates.businessLegalName;
		if (updates.dba !== undefined) requestBody.dba = updates.dba;
		if (updates.previousBusinessNames !== undefined)
			requestBody.previousBusinessNames = updates.previousBusinessNames;
		if (updates.entityType !== undefined)
			requestBody.entityType = updates.entityType;
		if (updates.countryOfIncorporation !== undefined)
			requestBody.countryOfIncorporation = updates.countryOfIncorporation;
		if (updates.registrationNumber !== undefined)
			requestBody.registrationNumber = updates.registrationNumber;
		if (updates.dateOfIncorporation !== undefined)
			requestBody.dateOfIncorporation = updates.dateOfIncorporation;
		if (updates.registeredAddress !== undefined)
			requestBody.registeredAddress = updates.registeredAddress;
		if (updates.website !== undefined) requestBody.website = updates.website;
		if (updates.businessDescription !== undefined)
			requestBody.businessDescription = updates.businessDescription;
		if (updates.expectedMonthlyVolume !== undefined)
			requestBody.expectedMonthlyVolume = updates.expectedMonthlyVolume;
		if (updates.proofOfBusinessUrl !== undefined)
			requestBody.proofOfBusinessUrl = updates.proofOfBusinessUrl;
		if (updates.proofOfAddressUrl !== undefined)
			requestBody.proofOfAddressUrl = updates.proofOfAddressUrl;
		if (updates.federalTaxId !== undefined)
			requestBody.federalTaxId = updates.federalTaxId;
		if (updates.vatId !== undefined) requestBody.vatId = updates.vatId;
		if (updates.rutDocument !== undefined)
			requestBody.rutDocument = updates.rutDocument;

		// Step 2 - Wallet & Technical Details
		if (updates.walletType !== undefined)
			requestBody.walletType = updates.walletType;
		if (updates.hostedStatus !== undefined)
			requestBody.hostedStatus = updates.hostedStatus;
		if (updates.currentlyIntegratedWithStellar !== undefined)
			requestBody.currentlyIntegratedWithStellar =
				updates.currentlyIntegratedWithStellar;
		if (updates.allowP2PInternal !== undefined)
			requestBody.allowP2PInternal = updates.allowP2PInternal;
		if (updates.allowP2PExternal !== undefined)
			requestBody.allowP2PExternal = updates.allowP2PExternal;
		// Map to DB schema: virtual_assets_info (not virtual_assets_details)
		if (updates.virtualAssetsDetails !== undefined)
			requestBody.virtualAssetsInfo = updates.virtualAssetsDetails;
		if (updates.publicKeySharing !== undefined)
			requestBody.publicKeySharing = updates.publicKeySharing;
		if (updates.isWalletOwnerKYCRequired !== undefined)
			requestBody.isWalletOwnerKYCRequired = updates.isWalletOwnerKYCRequired;
		if (updates.walletHolderInfoCollected !== undefined)
			requestBody.walletHolderInfoCollected = updates.walletHolderInfoCollected;
		if (updates.walletHolderInfoUpdateCircumstances !== undefined)
			requestBody.walletHolderInfoUpdateCircumstances =
				updates.walletHolderInfoUpdateCircumstances;
		if (updates.walletHolderInfoUpdateFrequency !== undefined)
			requestBody.walletHolderInfoUpdateFrequency =
				updates.walletHolderInfoUpdateFrequency;
		if (updates.isWalletHolderInfoUpdatedRegularly !== undefined)
			requestBody.isWalletHolderInfoUpdatedRegularly =
				updates.isWalletHolderInfoUpdatedRegularly;
		if (updates.omnibusAccountDescription !== undefined)
			requestBody.omnibusAccountDescription = updates.omnibusAccountDescription;
		if (updates.otherFundingMethods !== undefined)
			requestBody.otherFundingMethods = updates.otherFundingMethods;

		// Step 3 - Ownership & UBO. Both keys round-trip to root `tiers`
		// via kycFormDataToDbUpdates / the BFF camelToSnake mapping. The
		// older `ownershipData = { tiers: ... }` shape wrote to a
		// nonexistent `ownership_data` field — caused silent data loss for
		// every form whose read transform then returned [] and overwrote
		// the server's real owners on the next save.
		if (updates.numberOfOwnershipTiers !== undefined)
			requestBody.numberOfOwnershipTiers = updates.numberOfOwnershipTiers;
		if (updates.ownershipTiers !== undefined)
			requestBody.ownershipTiers = updates.ownershipTiers;
		if (updates.consentForBackgroundChecks !== undefined)
			requestBody.consentForBackgroundChecks =
				updates.consentForBackgroundChecks;
		if (updates.anyOwnerConvicted !== undefined)
			requestBody.anyOwnerConvicted = updates.anyOwnerConvicted;
		if (updates.convictionDetails !== undefined)
			requestBody.convictionDetails = updates.convictionDetails;

		// Step 4 - Compliance & Risk
		if (updates.isRegulatedEntity !== undefined)
			requestBody.isRegulatedEntity = updates.isRegulatedEntity;
		// Map to DB schema: licenses_registrations.licenses (not licenses)
		if (updates.licenses !== undefined)
			requestBody.licensesRegistrations = { licenses: updates.licenses };
		if (updates.complianceOfficer !== undefined)
			requestBody.complianceOfficer = updates.complianceOfficer;
		if (updates.sanctionsScreening !== undefined)
			requestBody.sanctionsScreening = updates.sanctionsScreening;
		if (updates.sanctionsVendor !== undefined)
			requestBody.sanctionsVendor = updates.sanctionsVendor;
		if (updates.sanctionsScreenedItems !== undefined)
			requestBody.sanctionsScreenedItems = updates.sanctionsScreenedItems;
		if (updates.blockchainMonitoring !== undefined)
			requestBody.blockchainMonitoring = updates.blockchainMonitoring;
		if (updates.monitoringVendor !== undefined)
			requestBody.monitoringVendor = updates.monitoringVendor;
		if (updates.monitoringProcess !== undefined)
			requestBody.monitoringProcess = updates.monitoringProcess;
		if (updates.monitoringThresholds !== undefined)
			requestBody.monitoringThresholds = updates.monitoringThresholds;
		if (updates.regulatoryEnforcement !== undefined)
			requestBody.regulatoryEnforcement = updates.regulatoryEnforcement;
		if (updates.enforcementDetails !== undefined)
			requestBody.enforcementDetails = updates.enforcementDetails;
		if (updates.riskAssessmentUrl !== undefined)
			requestBody.riskAssessmentUrl = updates.riskAssessmentUrl;
		if (updates.complianceProgramUrl !== undefined)
			requestBody.complianceProgramUrl = updates.complianceProgramUrl;

		// Step 5 - Limits & Restrictions
		if (updates.velocityLimits !== undefined)
			requestBody.velocityLimits = updates.velocityLimits;
		if (updates.transactionLimits !== undefined)
			requestBody.transactionLimits = updates.transactionLimits;
		if (updates.otherLimits !== undefined)
			requestBody.otherLimits = updates.otherLimits;
		if (updates.blockWalletHolders !== undefined)
			requestBody.blockWalletHolders = updates.blockWalletHolders;
		if (updates.blockingCircumstances !== undefined)
			requestBody.blockingCircumstances = updates.blockingCircumstances;
		if (updates.blockingPreventionControls !== undefined)
			requestBody.blockingPreventionControls =
				updates.blockingPreventionControls;

		// Step 6 - Contacts & Final Uploads
		if (updates.primaryBusinessContact !== undefined)
			requestBody.primaryBusinessContact = updates.primaryBusinessContact;
		// Send primaryProductContact; BFF maps to primary_technical_contact
		if (updates.primaryProductContact !== undefined)
			requestBody.primaryProductContact = updates.primaryProductContact;
		if (updates.primaryLegalContact !== undefined)
			requestBody.primaryLegalContact = updates.primaryLegalContact;
		if (updates.primarySigner !== undefined)
			requestBody.primarySigner = updates.primarySigner;
		if (updates.ownershipStructureChartUrl !== undefined)
			requestBody.ownershipStructureChartUrl =
				updates.ownershipStructureChartUrl;
		if (updates.hasBoardOfDirectors !== undefined)
			requestBody.hasBoardOfDirectors = updates.hasBoardOfDirectors;
		if (updates.boardOfDirectors !== undefined)
			requestBody.boardOfDirectors = updates.boardOfDirectors;

		// Metadata fields
		if (updates.walletId !== undefined) requestBody.walletId = updates.walletId;
		if (updates.status !== undefined) requestBody.status = updates.status;
		if (updates.currentStep !== undefined)
			requestBody.currentStep = updates.currentStep;
		if (updates.percentComplete !== undefined)
			requestBody.percentComplete = updates.percentComplete;
		if (updates.lastSavedAt !== undefined)
			requestBody.lastSavedAt = updates.lastSavedAt;
		if (updates.hiddenFieldValues !== undefined)
			requestBody.hiddenFieldValues = updates.hiddenFieldValues;

		// Interceptor unwraps { success, data } — result is the DBKYBForm directly
		const data = (await axiosClient.patch(
			`/api/kyb-forms/${id}`,
			requestBody,
		)) as unknown as DBKYBForm;
		return transformDBKYBToKYCFormData(data);
	},

	async submit(id: string): Promise<KYCFormData> {
		// Interceptor unwraps { success, data } — result is the DBKYBForm directly
		const data = (await axiosClient.post(
			`/api/kyb-forms/${id}/submissions`,
			{},
		)) as unknown as DBKYBForm;
		return transformDBKYBToKYCFormData(data);
	},

	async updateStatus(
		id: string,
		status: KYCFormData["status"],
	): Promise<KYCFormData> {
		// Interceptor unwraps { success, data } — result is the DBKYBForm directly
		const data = (await axiosClient.patch(`/api/kyb-forms/${id}`, {
			status: status,
		})) as unknown as DBKYBForm;
		return transformDBKYBToKYCFormData(data);
	},

	async addComment(
		formId: string,
		author: string,
		text: string,
		isInternal: boolean,
	): Promise<KYCFormData> {
		// Interceptor unwraps { success, data } — result is the DBKYBForm directly
		const data = (await axiosClient.post(`/api/kyb-forms/${formId}/comments`, {
			author,
			text,
			isInternal,
		})) as unknown as DBKYBForm;
		return transformDBKYBToKYCFormData(data);
	},
};
